import React, { useState, useRef, useEffect } from 'react';
import { GripVertical, X, Clock, Brain } from 'lucide-react';

/**
 * DraggableActivity - A drag-and-drop activity card with inline editing
 *
 * Features:
 * - Drag handle for reordering
 * - Click-to-edit for title, duration, description
 * - Keyboard support (Enter to save, Escape to cancel)
 * - Remove button
 */
const DraggableActivity = ({
  activity,
  phase,
  index,
  onMove,
  onUpdate,
  onRemove,
  isOnlyActivity = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [editing, setEditing] = useState(null); // 'title' | 'duration' | 'description'
  const [localValue, setLocalValue] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editing === 'title' || editing === 'duration') {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    } else if (editing === 'description') {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      }
    }
  }, [editing]);

  const startEdit = (field) => {
    setEditing(field);
    if (field === 'duration') {
      setLocalValue(activity.duration_minutes?.toString() || '5');
    } else {
      setLocalValue(activity[field] || '');
    }
  };

  const commitEdit = () => {
    if (!editing) return;

    let newValue = localValue;
    if (editing === 'duration') {
      newValue = parseInt(localValue) || 5;
      if (newValue < 1) newValue = 1;
      if (newValue > 60) newValue = 60;
    }

    if (newValue !== activity[editing === 'duration' ? 'duration_minutes' : editing]) {
      const updates = editing === 'duration'
        ? { duration_minutes: newValue }
        : { [editing]: newValue };
      onUpdate(activity.id, updates);
    }
    setEditing(null);
    setLocalValue('');
  };

  const cancelEdit = () => {
    setEditing(null);
    setLocalValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && editing !== 'description') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Enter' && editing === 'description' && e.shiftKey) {
      // Allow shift+enter for new lines in description
    } else if (e.key === 'Enter' && editing === 'description' && !e.shiftKey) {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Drag handlers
  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({
      activityId: activity.id,
      sourcePhase: phase,
      sourceIndex: index
    }));
    // Add a slight delay to show the ghost image
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.activityId !== activity.id) {
        onMove(data.activityId, data.sourcePhase, phase, index);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const activityTypeLabels = {
    hook: 'Hook',
    warm_up: 'Warm-up',
    review: 'Review',
    goal_setting: 'Goal Setting',
    introduction: 'Introduction',
    guided_practice: 'Guided Practice',
    independent_practice: 'Independent Practice',
    skill_building: 'Skill Building',
    game: 'Game',
    application: 'Application',
    reflection: 'Reflection',
    summary: 'Summary',
    preview: 'Preview'
  };

  return (
    <div
      className={`sp-draggable-activity ${isDragging ? 'sp-draggable-activity--dragging' : ''} ${isDragOver ? 'sp-draggable-activity--drag-over' : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Handle */}
      <div className="sp-draggable-activity__handle">
        <GripVertical size={16} />
      </div>

      {/* Main Content */}
      <div className="sp-draggable-activity__content">
        {/* Title Row */}
        <div className="sp-draggable-activity__title-row">
          {editing === 'title' ? (
            <input
              ref={inputRef}
              type="text"
              className="sp-inline-edit sp-inline-edit--editing sp-inline-edit--title"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleKeyDown}
              placeholder="Activity title"
            />
          ) : (
            <span
              className="sp-inline-edit sp-draggable-activity__title"
              onClick={() => startEdit('title')}
              title="Click to edit title"
            >
              {activity.title || 'Untitled Activity'}
            </span>
          )}

          {/* Duration */}
          <div className="sp-draggable-activity__duration-wrapper">
            <Clock size={12} />
            {editing === 'duration' ? (
              <input
                ref={inputRef}
                type="number"
                className="sp-inline-edit sp-inline-edit--editing sp-inline-edit--duration"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={handleKeyDown}
                min="1"
                max="60"
              />
            ) : (
              <span
                className="sp-inline-edit sp-draggable-activity__duration"
                onClick={() => startEdit('duration')}
                title="Click to edit duration"
              >
                {activity.duration_minutes} min
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="sp-draggable-activity__description-wrapper">
          {editing === 'description' ? (
            <textarea
              ref={textareaRef}
              className="sp-inline-edit sp-inline-edit--editing sp-inline-edit--description"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleKeyDown}
              placeholder="Activity description"
              rows={3}
            />
          ) : (
            <p
              className="sp-inline-edit sp-draggable-activity__description"
              onClick={() => startEdit('description')}
              title="Click to edit description"
            >
              {activity.description || 'Click to add description...'}
            </p>
          )}
        </div>

        {/* Expandable Details */}
        {(activity.neuro_technique || activity.rationale || activity.activity_type) && (
          <button
            className="sp-draggable-activity__toggle-details"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </button>
        )}

        {showDetails && (
          <div className="sp-draggable-activity__details">
            {activity.activity_type && (
              <span className="sp-draggable-activity__type-badge">
                {activityTypeLabels[activity.activity_type] || activity.activity_type}
              </span>
            )}
            {activity.neuro_technique && (
              <div className="sp-draggable-activity__neuro">
                <Brain size={12} />
                <span>{activity.neuro_technique}</span>
              </div>
            )}
            {activity.rationale && (
              <p className="sp-draggable-activity__rationale">
                <strong>Why:</strong> {activity.rationale}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Remove Button */}
      {!isOnlyActivity && (
        <button
          className="sp-draggable-activity__remove"
          onClick={() => onRemove(activity.id)}
          title="Remove activity"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export default DraggableActivity;
